import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import * as yup from "yup";
import { useFormik } from "formik";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment-timezone";
import uuidv4 from "uuid/v4";
import { useSelector } from "react-redux";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import mixpanel from "mixpanel-browser";

import * as constants from "../../../constants";
import { updateIncidentReport } from "../../../utils/appsyncOperations";
import LoadingButton from "../../Buttons/Loading";
import UserSearchDialog from "../UsersSearch";
import SearchPropertyResponsesDialog from "../../Dialogs/SearchPropertyResponses";
import MutateIncidentReportSubject from "../../Dialogs/MutateIncidentReportSubject";
import MutateIncidentReportVehicle from "../../Dialogs/MutateIncidentReportVehicle";

const initTime = (timezone) => ({
  start: moment().tz(timezone),
  end: moment().tz(timezone),
});

const CreateIncidentReport = ({ isOpen, onClose, propertyId }) => {
  const property = useSelector((state) => state.properties.byId[propertyId]);
  const usersById = useSelector((state) => state.users.activeById);
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const {
    serviceProviderId,
    place: { timezone },
  } = property;

  const [time, setTime] = useState(() => initTime(timezone));
  const [loading, setLoading] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState({ displayName: "", userId: "" });
  const [userError, setUserError] = useState(false);
  const [timeError, setTimeError] = useState(null);
  const [showSearchResponses, setShowSearchResponses] = useState(false);
  const [response, setResponse] = useState(null);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [subjects, setSubjects] = useState({});
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [vehicles, setVehicles] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    if (userId) setUser(usersById[userId]);
    else {
      setUser({ displayName: "", userId: "" });
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      formData.handleReset();
      setTime(() => initTime(timezone));
    }
  }, [isOpen]);

  const formData = useFormik({
    initialValues: {
      description: "",
      policeCase: {
        number: "",
        agency: "",
      },
      title: "",
    },
    onSubmit: (values) => formSubmit(values),
    validationSchema: yup.object({
      title: yup.string().max(280, "Must be less than 280 characters"),
      description: yup.string().max(2000, "Must be less than 2000 characters"),
      policeCase: yup.object({
        number: yup.string().max(140, "Must be less than 140 characters"),
        agency: yup.string().max(140, "Must be less than 140 characters"),
      }),
    }),
  });

  const formSubmit = async (values) => {
    if (!userId) {
      return setUserError(true);
    }

    const incidentInput = {
      description: values.description,
      policeCase:
        values.policeCase.number && values.policeCase.agency
          ? values.policeCase
          : null,
      title: values.title,
      incidentId: uuidv4(),
      propertyId,
      serviceProviderId,
      submitted: {
        at: Date.now(),
        by: userId,
      },
      edited: {
        at: Date.now(),
        by: userId,
      },
      created: {
        at: Date.now(),
        by: userId,
      },
      startTime: time.start.valueOf(),
      endTime: time.end.valueOf(),
      subjects:
        Object.values(subjects).length > 0
          ? Object.values(subjects).map((subject) => {
              const temp = subject;
              delete temp.id;
              return temp;
            })
          : null,
      vehicles:
        Object.values(vehicles).length > 0
          ? Object.values(vehicles).map((vehicle) => {
              const temp = vehicle;
              delete temp.id;
              return temp;
            })
          : null,
    };

    try {
      setLoading(true);
      await updateIncidentReport(incidentInput);
      mixpanel.track(constants.mixpanel.INCIDENT_REPORT_SUBMIT);
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error, { variant: "error" });
    }

    setLoading(false);
  };

  const renderSubjects = () =>
    Object.values(subjects).map((subject, index) => (
      <ListItem
        button
        onClick={() => {
          setShowSubjectModal(true);
          setSelectedSubject(subject);
        }}
      >
        <ListItemText primary={subject.name} />
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            onClick={() => {
              const temp = { ...subjects };
              delete temp[subject.id];
              setSubjects(temp);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    ));

  const renderVehicles = () =>
    Object.values(vehicles).map((vehicle, index) => (
      <ListItem
        button
        onClick={() => {
          setShowVehicleModal(true);
          setSelectedVehicle(vehicle);
        }}
      >
        <ListItemText primary={vehicle.description} />
        <ListItemSecondaryAction>
          <IconButton
            edge="end"
            onClick={() => {
              const temp = { ...vehicles };
              delete temp[vehicle.id];
              setVehicles(temp);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    ));

  const descriptionError = formData.touched.note && formData.errors.description;
  const policeCaseAgencyError =
    formData.touched.policeCase &&
    formData.touched.policeCase.agency &&
    formData.errors.policeCase &&
    formData.errors.policeCase.agency;
  const policeCaseNumberError =
    formData.touched.policeCase &&
    formData.touched.policeCase.number &&
    formData.errors.policeCase &&
    formData.errors.policeCase.number;
  const titleError = formData.touched.title && formData.errors.title;

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Create Incident Report</DialogTitle>
        <DialogContent>
          <List>
            <ListSubheader disableSticky>General</ListSubheader>
            <ListItem>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    required
                    onClick={() => setShowUserSearch(true)}
                    variant={"filled"}
                    value={user.displayName}
                    error={userError}
                    onChange={() => setUserError(false)}
                    helperText={userError ? "Required" : null}
                    label={"User"}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id={"title"}
                    variant={"filled"}
                    onBlur={formData.onBlur}
                    value={formData.values.title}
                    onChange={formData.handleChange}
                    error={titleError}
                    helperText={titleError ? formData.errors.title : null}
                    label={"Title"}
                  />
                </Grid>
              </Grid>
            </ListItem>
            <ListItem>
              <TextField
                fullWidth
                id={"description"}
                variant={"filled"}
                onBlur={formData.onBlur}
                value={formData.values.description}
                onChange={formData.handleChange}
                error={descriptionError}
                helperText={
                  descriptionError ? formData.errors.description : null
                }
                label={"Description"}
                multiline
              />
            </ListItem>
            <ListSubheader disableSticky>Duration</ListSubheader>
            {
              <ListItem>
                <Grid container spacing={2}>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <Grid item xs={6}>
                      <DateTimePicker
                        ampm={false}
                        format={constants.moment.format}
                        label="Started At"
                        inputVariant="filled"
                        value={time.start}
                        onChange={(start) => {
                          if (!start) return;
                          setTime({ ...time, start });
                          setTimeError(null);
                        }}
                        error={timeError}
                        helperText={timeError}
                        disableFuture
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DateTimePicker
                        ampm={false}
                        format={constants.moment.format}
                        label="Ended At"
                        inputVariant="filled"
                        value={time.end}
                        onChange={(end) => {
                          if (!end) return;
                          setTime({ ...time, end });
                          setTimeError(null);
                        }}
                        error={timeError}
                        helperText={timeError}
                        fullWidth
                        disableFuture
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                </Grid>
              </ListItem>
            }
            <ListSubheader disableSticky>Response</ListSubheader>
            {response ? (
              <ListItem>
                <ListItemText
                  primary={moment(response.createdAt)
                    .tz(property.place.timezone)
                    .format(constants.moment.format)}
                  secondary={`ID: ${response.instanceId}`}
                />
                <ListItemSecondaryAction>
                  <Button onClick={() => setResponse(null)} color="primary">
                    Clear
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ) : (
              <ListItem>
                <ListItemText secondary={"Attach response report"} />
                <ListItemSecondaryAction>
                  <Button
                    onClick={() => setShowSearchResponses(true)}
                    color="primary"
                  >
                    Search
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            )}
            <ListSubheader disableSticky>Police Case</ListSubheader>
            <ListItem>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id={"policeCase.agency"}
                    variant={"filled"}
                    onBlur={formData.onBlur}
                    value={formData.values.policeCase.agency}
                    onChange={formData.handleChange}
                    error={policeCaseAgencyError}
                    helperText={
                      policeCaseAgencyError
                        ? formData.errors.policeCase.agency
                        : null
                    }
                    label={"Police Agency"}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id={"policeCase.number"}
                    variant={"filled"}
                    onBlur={formData.onBlur}
                    value={formData.values.policeCase.number}
                    onChange={formData.handleChange}
                    error={policeCaseNumberError}
                    helperText={
                      policeCaseNumberError
                        ? formData.errors.policeCase.number
                        : null
                    }
                    label={"Case Number"}
                  />
                </Grid>
              </Grid>
            </ListItem>
            <ListSubheader disableSticky>Subjects</ListSubheader>
            <ListItem>
              <ListItemText
                secondary={`${Object.values(subjects).length} subjects added`}
              />
              <ListItemSecondaryAction>
                <Button
                  onClick={() => setShowSubjectModal(true)}
                  color="primary"
                >
                  Add
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
            {renderSubjects()}
            <ListSubheader disableSticky>Vehicles</ListSubheader>
            <ListItem>
              <ListItemText
                secondary={`${Object.values(vehicles).length} vehicles added`}
              />
              <ListItemSecondaryAction>
                <Button
                  onClick={() => setShowVehicleModal(true)}
                  color="primary"
                >
                  Add
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
            {renderVehicles()}
          </List>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={formData.handleSubmit}
            color="primary"
            isLoading={loading}
          >
            Create
          </LoadingButton>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {showSubjectModal && (
        <MutateIncidentReportSubject
          isOpen={showSubjectModal}
          onClose={() => {
            setShowSubjectModal(false);
          }}
          onSave={(subject) => {
            setSelectedSubject(null);
            setShowSubjectModal(false);
            setSubjects((prevState) => {
              const temp = { ...prevState };
              temp[subject.id] = subject;
              return temp;
            });
          }}
          name={selectedSubject ? selectedSubject.name : ""}
          sex={selectedSubject ? selectedSubject.sex : null}
          driverLicenseNumber={
            selectedSubject && selectedSubject.driverLicense
              ? selectedSubject.driverLicense.number
              : ""
          }
          driverLicenseState={
            selectedSubject && selectedSubject.driverLicense
              ? selectedSubject.driverLicense.state
              : null
          }
          ethnicity={selectedSubject ? selectedSubject.ethnicity : null}
          race={selectedSubject ? selectedSubject.race : null}
          phoneNumber={selectedSubject ? selectedSubject.phoneNumber : ""}
          address={selectedSubject ? selectedSubject.address : ""}
          description={selectedSubject ? selectedSubject.description : ""}
          type={selectedSubject ? selectedSubject.type : null}
          height={selectedSubject ? selectedSubject.height : ""}
          weight={selectedSubject ? selectedSubject.weight : ""}
          dateOfBirth={selectedSubject ? selectedSubject.dateOfBirth : ""}
          age={selectedSubject ? selectedSubject.age : ""}
          hairColor={selectedSubject ? selectedSubject.hairColor : null}
          eyeColor={selectedSubject ? selectedSubject.eyeColor : null}
          id={selectedSubject ? selectedSubject.id : uuidv4()}
        />
      )}
      {showVehicleModal && (
        <MutateIncidentReportVehicle
          isOpen={showVehicleModal}
          onClose={() => {
            setSelectedVehicle(null);
            setShowVehicleModal(false);
          }}
          onSave={(vehicle) => {
            setSelectedVehicle(null);
            setShowVehicleModal(false);
            setVehicles((prevState) => {
              const temp = { ...prevState };
              temp[vehicle.id] = vehicle;
              return temp;
            });
          }}
          description={selectedVehicle ? selectedVehicle.description : ""}
          make={selectedVehicle ? selectedVehicle.make : ""}
          model={selectedVehicle ? selectedVehicle.model : ""}
          year={selectedVehicle ? selectedVehicle.year : ""}
          color={selectedVehicle ? selectedVehicle.color : ""}
          vin={selectedVehicle ? selectedVehicle.vin : ""}
          registeredOwner={
            selectedVehicle ? selectedVehicle.registeredOwner : ""
          }
          type={selectedVehicle ? selectedVehicle.type : null}
          status={selectedVehicle ? selectedVehicle.status : null}
          vehicleLicenseNumber={
            selectedVehicle && selectedVehicle.vehicleLicense
              ? selectedVehicle.vehicleLicense.number
              : ""
          }
          vehicleLicenseState={
            selectedVehicle && selectedVehicle.vehicleLicense
              ? selectedVehicle.vehicleLicense.state
              : null
          }
          id={selectedVehicle ? selectedVehicle.id : uuidv4()}
        />
      )}
      <SearchPropertyResponsesDialog
        isOpen={showSearchResponses}
        onClose={() => setShowSearchResponses(false)}
        propertyId={propertyId}
        onSelectResponse={(response) => {
          setShowSearchResponses(false);
          setResponse(response);
        }}
      />
      <UserSearchDialog
        open={showUserSearch}
        onClose={() => setShowUserSearch(false)}
        onUserClick={(id) => {
          setUserError(false);
          setUserId(id);
          setShowUserSearch(false);
        }}
        showHours={false}
        users={Object.values(usersById)}
      />
    </>
  );
};

export default CreateIncidentReport;
